<template>
  <div 
    v-show="!loading"
    ref="bienvenidaRef"
    class="page-wrapper"
    :class="{ 'compact-view': asistenciaConfirmada || showQRCode }"
  >
    <div class="card-scene">
      
      <div ref="formCardRef" class="tarjeta-fisica">
        <div class="inner-border">
          
          <!-- Vista de agradecimiento final (Reemplazando el código QR) -->
          <div v-if="asistenciaConfirmada || showQRCode" class="qr-view text-center">
            <p class="qr-header" style="font-family: 'Playfair Display', serif; font-size: 26px; color: #7a5a35; margin-bottom: 20px;">¡Muchas Gracias!</p>
            <p class="qr-instructions" style="font-size: 16px; font-style: normal; color: #2c2c2c;">
              Tu confirmación de asistencia ha sido registrada con éxito.
            </p>
          </div>

          <div v-else class="form-content">
            <div class="form-header">
              <h2 ref="tituloAnimado" class="form-title">
                Confirmar<br><span class="subtitulo">Asistencia</span>
              </h2>
              
              <div class="progress-wrapper">
                <div class="progress-track">
                  <div 
                    class="progress-fill" 
                    :style="{ width: ((currentStep - 1) / (totalSteps - 1)) * 100 + '%' }"
                  ></div>
                </div>
                <span class="step-counter">PASO {{ currentStep }} / {{ totalSteps }}</span>
              </div>
            </div>

            <form @submit.prevent="handleSubmit" class="confirmation-form">
              <div v-if="currentStep === 1" class="step-box">
                <div class="form-group">
                  <label class="form-label">Nombre Completo</label>
                  <input v-model.trim="name" type="text" class="nice-input" placeholder="Escribe tu nombre" required>
                </div>
                <div class="form-group">
                  <label class="form-label">Teléfono</label>
                  <input v-model.trim="phone" type="tel" class="nice-input" placeholder="10 dígitos" required pattern="[0-9]{10}">
                </div>
              </div>

              <div v-if="currentStep === 2 && !isXvEvent" class="step-box">
                <label class="form-label centered">¿De parte de quién nos acompañas?</label>
                <div class="radio-grid">
                  <label class="radio-card" :class="{ active: invitedBy === 'novia' }">
                    <input v-model="invitedBy" type="radio" value="novia" hidden required>
                    <span>LA NOVIA</span>
                  </label>
                  <label class="radio-card" :class="{ active: invitedBy === 'novio' }">
                    <input v-model="invitedBy" type="radio" value="novio" hidden>
                    <span>EL NOVIO</span>
                  </label>
                </div>
              </div>

              <div v-if="currentStep === asistenciaStep" class="step-box">
                <label class="form-label centered">¿Podremos contar con tu presencia?</label>
                <div class="radio-grid">
                  <label class="radio-card confirm" :class="{ active: attending === 'si' }">
                    <input v-model="attending" type="radio" value="si" hidden required>
                    <span>SÍ, ASISTIRÉ</span>
                  </label>
                  <label class="radio-card deny" :class="{ active: attending === 'no' }">
                    <input v-model="attending" type="radio" value="no" hidden>
                    <span>LO SIENTO, NO PUEDO</span>
                  </label>
                </div>
              </div>

              <div v-if="currentStep === totalSteps && attending !== 'no'" class="step-box">
                <label class="form-label centered">Confirma el número de personas</label>
                <div class="select-wrapper">
                  <select v-model.number="guests" class="nice-select" required>
                    <option :value="null" disabled>Selecciona...</option>
                    <option v-for="n in guestsOptions" :key="n" :value="n">{{ n }} {{ n === 1 ? 'Persona' : 'Personas' }}</option>
                  </select>
                </div>
              </div>

              <div class="nav-buttons">
                <button type="button" class="btn-p" @click="prevStep" v-if="currentStep > 1">Regresar</button>
                <button v-if="currentStep < totalSteps" type="button" class="btn-p" @click="nextStep">Siguiente</button>
                <button v-if="currentStep === totalSteps" type="submit" class="btn-p" :disabled="isSubmitting">
                  <span v-if="!isSubmitting">CONFIRMAR</span>
                  <span v-else class="spinner-white"></span>
                </button>
              </div>

              <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, nextTick } from 'vue';
import axios from 'axios';
import baseUrl from '../api';
import gsap from 'gsap';
import Swal from 'sweetalert2';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default {
  name: 'AttendanceConfirmation',
  props: {
    idInvitado: { type: Number, required: true },
    nombre: { type: String, default: '' },
    idFestejado: { type: Number, default: null }
  },
  setup(props) {

    const name = ref('');
    const phone = ref('');
    const invitedBy = ref('');
    const attending = ref('');
    const guests = ref(null);
    const guestsOptions = ref([1, 2]);
    const loading = ref(true);
    const isSubmitting = ref(false);
    const errorMessage = ref('');
    const qrCode = ref(null);
    const showQRCode = ref(false);
    const asistenciaConfirmada = ref(false);
    const currentStep = ref(1);
    const formCardRef = ref(null);
    const tituloAnimado = ref(null);
    const lacreRef = ref(null);

    const isXvEvent = computed(() => {
      const eventName = props.nombre?.toLowerCase();
      return ['xv', 'ym'].includes(eventName);
    });

    const totalSteps = computed(() => isXvEvent.value ? 3 : 4);
    const asistenciaStep = computed(() => isXvEvent.value ? 2 : 3);

    const animateStep = () => {
      nextTick(() => {
        const box = formCardRef.value?.querySelector('.step-box');
        if (!box) return;

        gsap.fromTo(
          box,
          { opacity: 0, x: 20 },
          { opacity: 1, x: 0, duration: 0.5, ease: 'power2.out' }
        );
      });
    };

    const nextStep = async () => {

      if (currentStep.value === 1 && (!name.value.trim() || !phone.value.trim())) {
        errorMessage.value = 'Por favor completa tus datos.';
        return;
      }

      if (currentStep.value === 2 && !isXvEvent.value && !invitedBy.value) {
        errorMessage.value = 'Selecciona una opción.';
        return;
      }

      if (currentStep.value === asistenciaStep.value) {

        if (!attending.value) {
          errorMessage.value = 'Selecciona tu asistencia.';
          return;
        }

        if (attending.value === 'no') {
          currentStep.value = totalSteps.value;
          await nextTick();
          handleSubmit();
          return;
        }
      }

      errorMessage.value = '';

      if (currentStep.value < totalSteps.value) {
        currentStep.value++;
        animateStep();
      }
    };

    const prevStep = () => {
      if (currentStep.value > 1) {
        currentStep.value--;
        animateStep();
      }
    };

    const fetchGuestLimit = async () => {
      try {
        const response = await axios.get(
          `${baseUrl}/${props.nombre ? `${props.nombre}/` : ''}${props.idFestejado ? `${props.idFestejado}/` : ''}${props.idInvitado}`
        );

        const numPases = Number(response.data.num_pase);

        // Si no hay idFestejado o num_pase es indefinido/0, da opciones de 1 hasta 5 pases
        if (!props.idFestejado || !numPases || numPases <= 0) {
          guestsOptions.value = [1, 2, 3, 4, 5];
        } else {
          guestsOptions.value = Array.from({ length: numPases }, (_, i) => i + 1);
        }

      } catch (e) {
        console.error('Error fetching pases:', e);
        // Respaldo en caso de fallo: Opción hasta 5 personas
        guestsOptions.value = [1, 2, 3, 4, 5];
      } finally {
        loading.value = false;
      }
    };

    const handleSubmit = async () => {

      isSubmitting.value = true;
      errorMessage.value = '';

      try {

        const formData = {
          nombre_asistencia: name.value.trim(),
          telefono_invitado: phone.value.trim(),
          invitedby: isXvEvent.value ? 'novia' : invitedBy.value,
          asistira: attending.value,
          id_festejada: props.idFestejado,
          id_invitado: props.idInvitado,
          num_pases: Number(attending.value === 'no' ? 0 : (guests.value ?? 0)),
        };

        await axios.post(`${baseUrl}/asistencia`, formData);

        if (attending.value === 'no') {
          Swal.fire({
            icon: 'info',
            title: 'Gracias',
            text: 'Hemos registrado que no podrás asistir.',
            confirmButtonColor: '#7a5a35'
          });
        } else {
          Swal.fire({
            icon: 'success',
            title: '¡Confirmado!',
            text: 'Gracias por confirmar tu asistencia.',
            confirmButtonColor: '#7a5a35'
          });
        }

        // Activamos la pantalla final de Gracias limpia
        asistenciaConfirmada.value = true;
        showQRCode.value = true;

      } catch (e) {
        errorMessage.value = 'Error al enviar la confirmación';
      } finally {
        isSubmitting.value = false;
      }
    };

    // Quitamos la lógica de verificación automática para que no intente recargar el QR
    onMounted(async () => {
      fetchGuestLimit();
      animateStep();
    });

    return {
      name,
      phone,
      invitedBy,
      attending,
      guests,
      guestsOptions,
      loading,
      isSubmitting,
      errorMessage,
      isXvEvent,
      asistenciaConfirmada,
      qrCode,
      showQRCode,
      currentStep,
      nextStep,
      prevStep,
      handleSubmit,
      totalSteps,
      asistenciaStep,
      formCardRef,
      tituloAnimado,
      lacreRef
    };
  }
};
</script>

<style scoped>
.page-wrapper {
  width: 100%;
  min-height: 80vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(232, 230, 227, 0.95);
  padding: 60px 20px;
  perspective: 1200px;
}

.card-scene {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 420px;
}

.sello-lacre-img {
  width: 60px;
  height: auto;
  z-index: 10;
  margin-bottom: -30px;
  filter: drop-shadow(0 4px 6px rgba(0,0,0,0.15));
}

.tarjeta-fisica {
  background: #ffffff;
  width: 100%;
  padding: 10px;
  box-shadow: 0 15px 45px rgba(0,0,0,0.06);
  border-radius: 2px;
  background-image: url('https://www.transparenttextures.com/patterns/paper-fibers.png');
}

.inner-border {
  border: 1px solid #e8e4de;
  padding: 40px 25px;
  display: flex;
  flex-direction: column;
  min-height: 480px;
}

.form-title {
  font-family: 'Playfair Display', serif;
  font-size: 28px;
  text-align: center;
  color: #2c2c2c;
  line-height: 1.1;
  margin-bottom: 30px;
}

.subtitulo {
  font-size: 18px;
  color: #7a5a35;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.progress-wrapper {
  margin-bottom: 30px;
  text-align: center;
}

.progress-track {
  height: 2px;
  background: #e8e4de;
  width: 60%;
  margin: 0 auto 10px;
}

.progress-fill {
  height: 100%;
  background: #7a5a35;
  transition: width 0.4s ease;
}

.step-counter {
  font-family: 'Karla', sans-serif;
  font-size: 10px;
  letter-spacing: 2px;
  color: #aaa;
}

.form-group { margin-bottom: 20px; }

.form-label {
  font-family: 'Karla', sans-serif;
  font-size: 11px;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 10px;
  display: block;
}

.form-label.centered { text-align: center; margin-bottom: 25px; }

.nice-input {
  width: 100%;
  border: none;
  border-bottom: 1px solid #e8e4de;
  padding: 12px 0;
  font-family: 'Playfair Display', serif;
  font-size: 17px;
  color: #333;
  transition: border-color 0.3s;
  background: transparent;
}

.nice-input:focus {
  outline: none;
  border-color: #7a5a35;
}

.radio-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
}

.radio-card {
  border: 1px solid #e8e4de;
  padding: 18px 10px;
  text-align: center;
  cursor: pointer;
  font-family: 'Karla', sans-serif;
  font-size: 11px;
  letter-spacing: 1px;
  color: #383636;
  transition: all 0.3s;
}

.radio-card.active {
  background: #fdfaf7;
  border-color: #7a5a35;
  color: #7a5a35;
}

.nice-select {
  width: 100%;
  border: 1px solid #e8e4de;
  padding: 14px;
  font-family: 'Playfair Display', serif;
  background: transparent;
  border-radius: 0;
  appearance: none;
}

.nav-buttons {
  margin-top: auto;
  display: flex;
  justify-content: center;
  gap: 12px;
  padding-top: 35px;
}

.btn-p {
  min-width: 150px;
  padding: 14px 34px;
  border: none;
  border-radius: 50px;
  background: linear-gradient(135deg, #8c6b4f, #6f5238);
  color: #fff;
  font-family: 'Karla', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all .3s ease;
  box-shadow: 0 10px 25px rgba(111, 82, 56, .25);
}

.btn-p:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 16px 35px rgba(111, 82, 56, .35);
  background: linear-gradient(135deg, #9b7757, #7d5d40);
}

.btn-p:active:not(:disabled) {
  transform: scale(.97);
}

.btn-p:disabled {
  opacity: .7;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.nav-buttons .btn-p:first-child {
  background: #fff;
  color: #7a5a35;
  border: 1px solid #d9d2ca;
  box-shadow: none;
}

.nav-buttons .btn-p:first-child:hover {
  background: #f8f5f2;
  border-color: #7a5a35;
  box-shadow: 0 8px 20px rgba(0,0,0,.08);
}

.qr-instructions {
  font-family: 'Karla', sans-serif;
  font-size: 13px;
  color: #3d3b3b;
  font-style: italic;
  margin-top: 15px;
}

.spinner-white {
  width: 15px;
  height: 15px;
  border: 2px solid #ffffff;
  border-bottom-color: transparent;
  border-radius: 50%;
  display: inline-block;
  animation: rotation 1s linear infinite;
}

@keyframes rotation {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-text {
  color: #c0392b;
  font-size: 11px;
  text-align: center;
  margin-top: 15px;
}
</style>